import { useMemo, useRef, useState } from 'react';
import { AccessTime } from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  InputAdornment,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { generateTimeSlots, getNextTimeSlot } from '@juno/utils';
import { commonTextFieldSx, getMenuItemSx } from './utils';

type PickerProps = {
  value: null | string | number | undefined;
  onChange: (val: string) => void;
};

export const StartTimePicker: React.FC<PickerProps> = ({ value, onChange }) => {
  const timeOptions = useMemo(() => generateTimeSlots(30), []);
  const defaultStartTime = getNextTimeSlot(30);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLElement | null>(null);

  let startTime = defaultStartTime;
  if (value) {
    const dateVal = new Date(value);
    const formatted = dateVal.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    if (timeOptions.includes(formatted)) startTime = formatted;
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <TextField
        inputRef={inputRef}
        label='Start Time'
        aria-label='Start Time'
        value={startTime}
        onClick={handleClick}
        variant='outlined'
        fullWidth
        sx={{
          mb: 2,
          ...commonTextFieldSx,
        }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position='end'>
              <AccessTime sx={{ cursor: 'pointer' }} />
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ zIndex: 1, position: 'relative' }}>
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement='bottom-start'
          disablePortal
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Box
              sx={{
                bgcolor: 'white',
                border: '1px solid #ccc',
                boxShadow: 3,
                p: 1,
                width: inputRef.current?.offsetWidth || 300,
                maxHeight: 300,
                overflowY: 'auto',
                zIndex: 1300,
              }}
            >
              <Typography variant='caption' fontWeight='bold'>
                Select Time
              </Typography>
              <Box>
                {timeOptions.map((time) => {
                  const isSelected = time === startTime;
                  return (
                    <Box
                      key={time}
                      onClick={() => {
                        onChange(time);
                        setOpen(false);
                      }}
                      ref={(el) => {
                        if (isSelected && el instanceof HTMLElement) {
                          el.scrollIntoView({ block: 'center' });
                        }
                      }}
                      sx={{
                        px: 1,
                        py: 1,
                        borderRadius: 1,
                        fontSize: 'medium',
                        cursor: 'pointer',
                        ...getMenuItemSx(isSelected),
                      }}
                    >
                      {time}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </ClickAwayListener>
        </Popper>
      </Box>
    </>
  );
};
